import React, { useCallback, useEffect, useState } from 'react'
import Cache from '@fs/zion-cache'

const cache = new Cache({ storeName: 'infoSheetOrientation' })
const CACHE_KEY = 'hey_should_i_use_bottom_sheet_here?'

/**
 * Custom hook to manage and cache the orientation state of an InfoSheet component.
 *
 * This hook provides a state variable to determine if the information sheet
 * should be displayed as a bottom sheet, and a function to update this state.
 * The state is cached using the Cache functionality from the @fs/zion-cache package
 * to retain the orientation preference across sessions.
 *
 * @returns {Object} An object containing:
 *   - {boolean} isBottomSheet: A boolean indicating if the InfoSheet
 *     is currently set to be displayed as a bottom sheet.
 *   - {Function} setInfoSheetOrientation: A function to update the orientation
 *     state and cache the new preference. Accepts a boolean parameter
 *     `shouldBeBottomSheet` to set the desired orientation.
 */
const useInfoSheetOrientationCachedState = (defaultValue = false) => {
  const [isBottomSheet, setIsBottomSheet] = useState(defaultValue)
  // Track if component is mounted to prevent state updates after unmount (prevents memory leaks)
  const mountedRef = React.useRef(true)

  useEffect(() => {
    // Reset mounted state for each effect run (critical for React Strict Mode double-invocation)
    mountedRef.current = true

    // Load cached orientation state on mount; fall back to defaultValue when no preference is cached
    cache
      .getItem(CACHE_KEY)
      .then((cachedGoal) => {
        if (mountedRef.current) {
          setIsBottomSheet(cachedGoal ?? defaultValue)
        }
      })
      .catch((error) => {
        console.error('Failed to load cached info sheet orientation:', error)
        // Continue with default value (false) on error
      })

    return () => {
      mountedRef.current = false
    }
  }, [])

  const setInfoSheetOrientation = useCallback((shouldBeBottomSheet) => {
    cache.setItem(CACHE_KEY, shouldBeBottomSheet)
    setIsBottomSheet(shouldBeBottomSheet)
  }, [])

  return { isBottomSheet, setInfoSheetOrientation }
}

export default useInfoSheetOrientationCachedState
